import {
  BarChart2,
  Building,
  CreditCard,
  DollarSign,
  FileText,
  Lock,
  Merge,
  Shield,
  Star,
  TrendingUp,
  Users,
  Wrench,
  Zap,
} from 'lucide-react';
import type { FAQItem } from './faq/types';
import { onboardingFAQs } from './faq/onboarding';
import { featuresFAQs } from './faq/features';
import { vendorsFAQs } from './faq/vendors';
import { documentsFAQs } from './faq/documents';
import { analyticsFAQs } from './faq/analytics';
import { securityFAQs } from './faq/security';
import { pricingFAQs } from './faq/pricing';
import { dataFAQs } from './faq/data';
import { intercommunityFAQs } from './faq/intercommunity';
import { treasuryFAQs } from './faq/treasury';
import { paymentsFAQs } from './faq/payments';
import { governanceFAQs } from './faq/governance';
import { facilitiesFAQs } from './faq/facilities';
import { easeFAQs } from './faq/ease';

export const faqData: FAQItem[] = [
  ...onboardingFAQs,
  ...featuresFAQs,
  ...vendorsFAQs,
  ...documentsFAQs,
  ...analyticsFAQs,
  ...securityFAQs,
  ...pricingFAQs,
  ...dataFAQs,
  ...intercommunityFAQs,
  ...treasuryFAQs,
  ...paymentsFAQs,
  ...governanceFAQs,
  ...facilitiesFAQs,
  ...easeFAQs,
];

export const categories = {
  onboarding: { title: 'Getting Started', icon: Zap, color: 'text-green-600' },
  features: { title: 'Platform Features', icon: Star, color: 'text-blue-600' },
  pricing: { title: 'Pricing & Contracts', icon: DollarSign, color: 'text-purple-600' },
  governance: { title: 'Governance & Legal', icon: Users, color: 'text-indigo-600' },
  ease: { title: 'Training & Support', icon: Shield, color: 'text-amber-600' },
  facilities: { title: 'Facilities', icon: Building, color: 'text-emerald-600' },
  vendors: { title: 'Vendors', icon: Wrench, color: 'text-rose-600' },
  documents: { title: 'Documents', icon: FileText, color: 'text-slate-700' },
  analytics: { title: 'Analytics', icon: BarChart2, color: 'text-fuchsia-600' },
  security: { title: 'Security', icon: Lock, color: 'text-cyan-700' },
  payments: { title: 'Payments', icon: CreditCard, color: 'text-sky-700' },
  treasury: { title: 'Treasury', icon: TrendingUp, color: 'text-emerald-700' },
  data: { title: 'Data Management', icon: FileText, color: 'text-slate-700' },
  intercommunity: { title: 'Inter-Community Commerce', icon: Merge, color: 'text-teal-700' },
} as const;

export type CategoryKey = keyof typeof categories;

export const categoryDescriptions: Partial<Record<CategoryKey, string>> = {
  onboarding: 'Getting started: contracting, setup, training, and go-live expectations.',
  features: 'Product capabilities across governance, finance, ops, and marketplace.',
  pricing: 'Setup fees, recurring costs, terms, and what’s included by tier.',
  governance: 'Voting, bylaws mapping, meetings, and compliance/audit trails.',
  ease: 'Training, support options, and adoption strategies for residents.',
  facilities: 'Amenity booking, fees, rules, limits, deposits, and cancellations.',
  vendors: 'Vendor portal, onboarding, bids, compliance, ratings, and payouts.',
  documents: 'Document vault, access controls, versioning, retention, and holds.',
  analytics: 'Dashboards, KPIs, exports, and scheduling for board packets.',
  security: 'Encryption, RBAC, secure docs, audit logs, SLAs, and data residency.',
  payments: 'ACH/cards, fee handling models, disputes/chargebacks, and refunds.',
  treasury: 'Treasury workflows, governance controls, and partner integrations.',
  data: 'Backups, snapshots, RPO/RTO, access boundaries, and deletion lifecycle.',
  intercommunity: 'Cross-community group buys, settlements, privacy, and disputes.',
};
